import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import Table from '../../../../components/Table';
import Pagination from '../../../../components/Pagination';
import TableSkeleton from '../../../../components/TableSkeleton';
import { PlusIcon } from '../../../../components/IconButton/icons';
import ListPage, { SearchInput } from '../../../../components/ListPage';
import { useServerFiltering } from '../../../../utils/useServerFiltering';
import { useUrlFilters } from '../../../../utils/useUrlFilters';
import ClientColumn from '../ClientColumn';
import AddClientModal from '../AddClientModal';
import ClientDetailModal from '../ClientDetailModal';
import {
  ClientWithCount,
  clientColumns,
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_DELAY
} from '../../config';

const CLIENT_FILTER_DEFINITIONS = {
  search: { param: 'cq', defaultValue: '', type: 'string' as const },
  page: { param: 'cpage', defaultValue: 1, type: 'number' as const },
  pageSize: { param: 'csize', defaultValue: 25, type: 'number' as const },
};

interface ClientsViewProps {
  viewToggle: React.ReactNode;
}

const ClientsView: React.FC<ClientsViewProps> = ({ viewToggle }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const {
    filters: urlFilters,
    setFilter,
    setFilters: setUrlFilters,
    resetFilters: resetUrlFilters,
    hasActiveFilters
  } = useUrlFilters(CLIENT_FILTER_DEFINITIONS);

  const searchInput = urlFilters.search;
  const urlPage = urlFilters.page;
  const urlPageSize = urlFilters.pageSize;

  const {
    data: clients,
    pagination,
    loading,
    filtering,
    error,
    setFilters,
    refetch
  } = useServerFiltering<ClientWithCount>({
    rpcFunction: 'get_filtered_clients',
    defaultFilters: {
      search_term: null,
      page_size: DEFAULT_PAGE_SIZE,
      page_offset: 0,
      sort_by: 'company_name',
      sort_order: 'asc'
    },
    debounceDelay: SEARCH_DEBOUNCE_DELAY
  });

  useEffect(() => {
    const pageSize = urlPageSize || DEFAULT_PAGE_SIZE;
    const pageOffset = ((urlPage || 1) - 1) * pageSize;

    setFilters({
      search_term: searchInput || null,
      page_size: pageSize,
      page_offset: pageOffset
    });
  }, [searchInput, urlPage, urlPageSize, setFilters]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ search: e.target.value, page: 1 });
  }, [setUrlFilters]);

  const handleClearFilters = useCallback(() => {
    resetUrlFilters();
  }, [resetUrlFilters]);

  const handleClearSearch = useCallback(() => {
    setUrlFilters({ search: '', page: 1 });
  }, [setUrlFilters]);

  const handlePageChange = useCallback((page: number) => {
    setFilter('page', page);
  }, [setFilter]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setUrlFilters({ pageSize: newPageSize, page: 1 });
  }, [setUrlFilters]);

  const handleClientCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleRowClick = useCallback((row: ClientWithCount) => {
    setSelectedClientId(row.id);
    setIsDetailModalOpen(true);
  }, []);

  const handleDetailModalClose = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedClientId(null);
  }, []);

  const handleClientUpdated = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleClientDeleted = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedClientId(null);
    refetch();
  }, [refetch]);

  const columns = useMemo<ColumnDef<ClientWithCount>[]>(
    () => {
      const columnConfig: Record<string, { minWidth?: string; width?: string; maxWidth?: string; align?: 'left' | 'right' | 'center' }> = {
        company_name: { minWidth: '240px', width: 'auto' },
        location: { minWidth: '160px', width: '24%' },
        contact_count: { minWidth: '112px', width: '112px', align: 'right' },
        created_at: { minWidth: '112px', width: '112px' }
      };

      return Object.entries(clientColumns)
        .sort(([, a], [, b]) => (a.order || 999) - (b.order || 999))
        .map(([key, config]) => ({
          header: config.label || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
          accessorKey: key,
          meta: columnConfig[key],
          cell: ({ row }: { row: any }) => (
            <ClientColumn
              column={key}
              config={config}
              row={row}
            />
          )
        }));
    },
    []
  );

  const currentPage = pagination?.page || urlPage || 1;
  const currentPageSize = pagination?.page_size || urlPageSize || DEFAULT_PAGE_SIZE;

  const initialLoading = loading && !clients.length;

  return (
    <ListPage
      title="Contacts"
      className="contacts-page"
      loading={initialLoading}
      skeleton={{ segmentCount: 2, filterCount: 0, showAction: true }}
      search={
        <SearchInput
          placeholder="Search clients…"
          value={searchInput}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          aria-label="Search clients"
        />
      }
      actions={
        <button type="button" className="pill-btn pill-btn--solid pill-btn--sm" onClick={() => setIsAddModalOpen(true)} disabled={loading}>
          <PlusIcon /> Add client
        </button>
      }
      segments={viewToggle}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      resultsText={
        !loading && pagination && (
          <>
            Showing {pagination.total_records > 0 ? ((currentPage - 1) * currentPageSize) + 1 : 0}–{Math.min(currentPage * currentPageSize, pagination.total_records)} of {pagination.total_records.toLocaleString()} clients
          </>
        )
      }
      footer={
        <Pagination
          variant="bar"
          currentPage={currentPage}
          totalPages={pagination?.total_pages ?? 1}
          totalRecords={pagination?.total_records ?? 0}
          pageSize={currentPageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          hasNext={pagination?.has_next ?? false}
          hasPrevious={pagination?.has_previous ?? false}
          loading={loading || filtering}
          showPageInfo
          pageSizeOptions={[25, 50, 100]}
        />
      }
    >
      {error && (
        <div className="list-page__error">
          Error loading clients: {error}
        </div>
      )}

      {initialLoading ? (
        <TableSkeleton compact rows={14} columns={4} showHeader />
      ) : clients.length === 0 ? (
        <div className="list-page__empty">
          {hasActiveFilters ? 'No clients match this search.' : 'No clients yet. Add a client to get started.'}
        </div>
      ) : (
        <div className={`list-page__body ${filtering ? 'is-filtering' : ''}`.trim()}>
          <Table data={clients} columns={columns} onRowClick={handleRowClick} stickyHeader />
        </div>
      )}

      <AddClientModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onClientCreated={handleClientCreated}
      />

      <ClientDetailModal
        isOpen={isDetailModalOpen}
        clientId={selectedClientId}
        onClose={handleDetailModalClose}
        onClientUpdated={handleClientUpdated}
        onClientDeleted={handleClientDeleted}
      />
    </ListPage>
  );
};

export default ClientsView;
